import { LitElement, css, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

/**
 * <sos-responsavel> — painel do RESPONSÁVEL (pai/mãe).
 *
 * Componente PRÓPRIO, e não um "modo" do <sos-dashboard>, pela mesma razão que o
 * backend ganhou router próprio: o painel de gestão tem Configurações, Conect e
 * Relatório, que o pai não pode alcançar. Um componente separado não consegue
 * renderizar essas seções nem por engano — a restrição é estrutural, não um `if`
 * que alguém remove sem perceber.
 *
 * Fala só com `/api/v1/dashboard/responsavel/*`, cujo escopo vem da claim
 * ASSINADA `children` do token. O componente NUNCA escolhe qual aluno olhar por
 * conta própria: ele só oferece os filhos que o backend devolveu.
 */

interface EscolaRow {
  id: string;
  nome: string;
}

interface FilhoRow {
  sub: string;
  nome: string | null;
  escola_id: string | null;
}

interface Contexto {
  escolas: EscolaRow[];
  filhos: FilhoRow[];
}

/** O que o painel do pai mostra. O payload é o MESMO do painel da escola
 * (ParceiroDashboard) — aqui declaramos só os campos que fazem sentido para UM
 * estudante. Ficam de fora, de propósito, as métricas de COORTE (matriculados,
 * ranking de estudantes, por_etapa): num filho só, comparar com ninguém não diz
 * nada e o pai não deve enxergar a turma. */
interface PainelFilho {
  escola: string | null;
  sessoes_tutor_7d: number;
  mensagens_alunos_7d: number;
  tempo_total_min: number;
  tempo_medio_sessao_min: number;
  media_msgs_por_sessao: number;
  recorrencia_media_dias: number;
  bloqueios_total: number;
  // Tendência vs. o período anterior de mesma duração (null = sem base).
  delta_sessoes: number | null;
  delta_interacoes: number | null;
  delta_barrados: number | null;
  prev_sessoes: number | null;
  prev_interacoes: number | null;
  prev_barrados: number | null;
  por_categoria_bloqueio: { categoria: string; rotulo: string; total: number }[];
  // ATENÇÃO ao nome do campo: a API devolve `materia`/`tematica`, não `nome`.
  // Declarar `nome` aqui fazia a lista renderizar linhas em branco.
  por_materia: { materia: string; total: number }[];
  por_tematica: { tematica: string; total: number }[];
  assuntos_emergentes: {
    materia: string;
    atual: number;
    anterior: number;
    growth_pct: number | null;
  }[];
  serie_temporal: { data: string; interacoes: number; alertas: number }[];
}

interface AlertaRow {
  categoria: string;
  rotulo: string;
  acao: string;
  created_at: string;
  mensagem: string | null;
}

/** Presets do seletor de período — os MESMOS rótulos do painel da escola. */
const PERIODOS: { v: string; rot: string }[] = [
  { v: 'mes_atual', rot: 'Mês atual' },
  { v: 'mes_passado', rot: 'Mês passado' },
  { v: '7d', rot: 'Últimos 7 dias' },
  { v: '30d', rot: 'Últimos 30 dias' },
  { v: '90d', rot: 'Últimos 90 dias' },
  { v: 'ano', rot: 'Este ano' },
  { v: 'tudo', rot: 'Todo período' },
];

@customElement('sos-responsavel')
export class SosResponsavel extends LitElement {
  @property({ attribute: 'api-base' }) apiBase = '';
  @property() token = '';
  @property({ attribute: 'token-url' }) tokenUrl = '';

  @state() private loading = true;
  @state() private erro = '';
  @state() private ctx?: Contexto;
  @state() private escolaId = '';
  @state() private filhoSub = '';
  @state() private dados?: PainelFilho;
  @state() private carregandoPainel = false;
  @state() private periodo = 'mes_atual';
  @state() private alertas?: AlertaRow[];
  @state() private carregandoAlertas = false;
  @state() private baixandoPdf = false;

  static styles = css`
    :host {
      display: block;
      --accent: #ff6f43;
      --ink: #062e51;
      --muted: #6b7c8d;
      --page: #f2f9ff;
      --line: #e3ecf5;
      font-family: Inter, system-ui, -apple-system, 'Segoe UI', sans-serif;
      color: var(--ink);
      line-height: 1.5;
    }
    .bar {
      display: flex;
      flex-wrap: wrap;
      gap: 12px;
      align-items: flex-end;
      margin-bottom: 20px;
    }
    .campo {
      display: flex;
      flex-direction: column;
      gap: 5px;
    }
    .campo label {
      font-size: 11.5px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.8px;
      color: var(--accent);
    }
    select {
      padding: 9px 13px;
      border: 1.5px solid var(--line);
      border-radius: 10px;
      background: #fff;
      font: inherit;
      font-size: 14.5px;
      color: var(--ink);
      min-width: 210px;
    }
    .grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(168px, 1fr));
      gap: 12px;
      margin-bottom: 22px;
    }
    .kpi {
      background: #fff;
      border: 1px solid var(--line);
      border-radius: 14px;
      padding: 16px 18px;
    }
    .kpi .rot {
      font-size: 12.5px;
      color: var(--muted);
      margin-bottom: 6px;
    }
    .kpi .val {
      font-size: 27px;
      font-weight: 700;
      letter-spacing: -0.5px;
    }
    .kpi .un {
      font-size: 13px;
      font-weight: 500;
      color: var(--muted);
      margin-left: 3px;
    }
    .sec {
      font-size: 11.5px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 1px;
      color: var(--accent);
      margin: 24px 0 10px;
    }
    .lista {
      background: #fff;
      border: 1px solid var(--line);
      border-radius: 14px;
      padding: 6px 18px;
    }
    .linha {
      display: flex;
      justify-content: space-between;
      gap: 12px;
      padding: 11px 0;
      border-bottom: 1px solid #f0f5fa;
      font-size: 14.5px;
    }
    .linha:last-child {
      border-bottom: 0;
    }
    .linha b {
      font-variant-numeric: tabular-nums;
    }
    .vazio,
    .msg {
      background: #fff;
      border: 1px solid var(--line);
      border-radius: 14px;
      padding: 28px 20px;
      text-align: center;
      color: var(--muted);
      font-size: 14.5px;
    }
    .err {
      background: #fff4f2;
      border-color: #ffd9d0;
      color: #b42318;
    }
    .dim {
      opacity: 0.5;
    }
    .titulo {
      display: flex;
      flex-wrap: wrap;
      align-items: baseline;
      gap: 10px;
      margin-bottom: 14px;
    }
    .titulo b {
      font-size: 18px;
      letter-spacing: -0.3px;
    }
    .titulo span {
      font-size: 13px;
      color: var(--muted);
    }
    .btn-pdf {
      margin-left: auto;
      padding: 8px 14px;
      border: 1.5px solid var(--line);
      border-radius: 10px;
      background: #fff;
      color: var(--ink);
      font: inherit;
      font-size: 13.5px;
      font-weight: 600;
      cursor: pointer;
    }
    .btn-pdf:hover:not(:disabled) {
      border-color: var(--accent);
      color: var(--accent);
    }
    .btn-pdf:disabled {
      opacity: 0.6;
      cursor: default;
    }
    .kpi .delta {
      margin-top: 7px;
      font-size: 12px;
      font-weight: 600;
      display: flex;
      flex-wrap: wrap;
      align-items: baseline;
      gap: 5px;
    }
    .kpi .delta.up {
      color: #1f8a5b;
    }
    .kpi .delta.down {
      color: #b42318;
    }
    .kpi .delta span {
      font-weight: 400;
      color: var(--muted);
    }
    .card {
      background: #fff;
      border: 1px solid var(--line);
      border-radius: 14px;
      padding: 18px;
    }
    .barras {
      display: flex;
      align-items: flex-end;
      gap: 6px;
      overflow-x: auto;
    }
    .col {
      flex: 1 1 0;
      max-width: 64px;
      min-width: 22px;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 6px;
    }
    .tubo {
      width: 100%;
      height: 110px;
      display: flex;
      align-items: flex-end;
      background: var(--page);
      border-radius: 6px;
      /* visible (e não hidden) porque o pino de alerta fica ACIMA do topo da
         barra; com hidden ele era cortado justamente no dia que importa. */
      overflow: visible;
      margin-top: 12px;
    }
    .preenche {
      position: relative;
      width: 100%;
      background: var(--accent);
      border-radius: 6px 6px 0 0;
    }
    .eixo {
      font-size: 10.5px;
      color: var(--muted);
      white-space: nowrap;
    }
    .kpi .ssub {
      margin-top: 6px;
      font-size: 12px;
      color: var(--muted);
    }
    .kpi.clicavel {
      cursor: pointer;
    }
    .kpi.clicavel:hover,
    .kpi.aberto {
      border-color: var(--accent);
    }
    .kpi .rot {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 8px;
    }
    .kpi .seta {
      color: var(--accent);
      font-size: 16px;
      line-height: 1;
    }
    .drill {
      background: #fff;
      border: 1px solid var(--line);
      border-radius: 14px;
      padding: 4px 18px;
      margin: -8px 0 22px;
    }
    .arow {
      padding: 13px 0;
      border-bottom: 1px solid #f0f5fa;
    }
    .arow:last-child {
      border-bottom: 0;
    }
    .atopo {
      display: flex;
      flex-wrap: wrap;
      align-items: baseline;
      gap: 9px;
      margin-bottom: 5px;
    }
    .atag {
      font-size: 11.5px;
      font-weight: 700;
      color: #b42318;
      background: #fff4f2;
      border: 1px solid #ffd9d0;
      border-radius: 20px;
      padding: 2px 9px;
    }
    .aquando {
      font-size: 12px;
      color: var(--muted);
      font-variant-numeric: tabular-nums;
    }
    .amsg {
      font-size: 14.5px;
      line-height: 1.45;
    }
    .amsg.vazia {
      color: var(--muted);
      font-style: italic;
    }
    .qtd {
      font-size: 11px;
      font-weight: 700;
      color: var(--ink);
      font-variant-numeric: tabular-nums;
    }
    .col.fantasma {
      visibility: hidden;
    }
    /* Marca do dia com alerta: um pino no topo da barra. Antes a barra INTEIRA
       ficava vermelha, o que lia como "o dia todo foi problema". */
    .pino {
      position: absolute;
      top: -9px;
      left: 50%;
      transform: translateX(-50%);
      min-width: 16px;
      height: 16px;
      border-radius: 9px;
      background: #b42318;
      color: #fff;
      font-size: 10px;
      font-weight: 700;
      line-height: 16px;
      text-align: center;
      padding: 0 4px;
    }
    .alta {
      color: #1f8a5b;
      display: flex;
      align-items: baseline;
      gap: 7px;
    }
    .alta small {
      color: var(--muted);
      font-weight: 400;
      font-variant-numeric: tabular-nums;
    }
    .vazio.ok {
      background: #f2fbf6;
      border-color: #cfeadd;
      color: #1f6f4c;
    }
  `;

  connectedCallback(): void {
    super.connectedCallback();
    void this.carregarContexto();
  }

  // ── token (mesmo contrato do <sos-dashboard>) ──────────────────────────────
  private _refresh: Promise<boolean> | null = null;

  private async refreshToken(): Promise<boolean> {
    if (!this.tokenUrl) return false;
    if (this._refresh) return this._refresh;
    this._refresh = (async () => {
      try {
        const r = await fetch(this.tokenUrl, { credentials: 'same-origin' });
        if (!r.ok) return false;
        const j = (await r.json()) as { token?: string };
        if (!j?.token) return false;
        this.token = j.token;
        return true;
      } catch {
        return false;
      } finally {
        setTimeout(() => (this._refresh = null), 0);
      }
    })();
    return this._refresh;
  }

  private async authFetch(url: string): Promise<Response> {
    const go = () =>
      fetch(url, { headers: { Authorization: `Bearer ${this.token}` } });
    let r = await go();
    if (r.status === 401 && (await this.refreshToken())) r = await go();
    return r;
  }

  private get base(): string {
    return `${this.apiBase}/api/v1/dashboard/responsavel`;
  }

  // ── carga ─────────────────────────────────────────────────────────────────
  private async carregarContexto(): Promise<void> {
    this.loading = true;
    this.erro = '';
    try {
      const r = await this.authFetch(`${this.base}/contexto`);
      if (r.status === 403) {
        this.erro =
          'Esta área é do responsável. Sua conta não tem estudantes vinculados.';
        return;
      }
      if (!r.ok) {
        this.erro = 'Não foi possível carregar seus dados agora.';
        return;
      }
      const ctx = (await r.json()) as Contexto;
      this.ctx = ctx;
      this.escolaId = ctx.escolas[0]?.id ?? '';
      this.filhoSub = this.filhosDaEscola()[0]?.sub ?? '';
      if (this.filhoSub) await this.carregarPainel();
    } catch {
      this.erro = 'Sem conexão com o servidor. Tente de novo em instantes.';
    } finally {
      this.loading = false;
    }
  }

  private filhosDaEscola(): FilhoRow[] {
    const f = this.ctx?.filhos ?? [];
    if (!this.escolaId) return f;
    return f.filter((x) => x.escola_id === this.escolaId);
  }

  /** Preset -> janela [from, to] em YYYY-MM-DD. Mesma conversão do painel da
   * escola, pro pai e a coordenação lerem o MESMO número no mesmo período. */
  private periodoRange(): { from: string; to: string } {
    const now = new Date();
    const D = (d: Date) =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
        d.getDate()
      ).padStart(2, '0')}`;
    const desde = (dias: number) => {
      const d = new Date(now);
      d.setDate(d.getDate() - dias);
      return D(d);
    };
    switch (this.periodo) {
      case 'mes_passado':
        return {
          from: D(new Date(now.getFullYear(), now.getMonth() - 1, 1)),
          to: D(new Date(now.getFullYear(), now.getMonth(), 0)),
        };
      case '7d':
        return { from: desde(6), to: D(now) };
      case '30d':
        return { from: desde(29), to: D(now) };
      case '90d':
        return { from: desde(89), to: D(now) };
      case 'ano':
        return { from: D(new Date(now.getFullYear(), 0, 1)), to: D(now) };
      case 'tudo':
        return { from: '2000-01-01', to: D(now) };
      default:
        return {
          from: D(new Date(now.getFullYear(), now.getMonth(), 1)),
          to: D(now),
        };
    }
  }

  private periodoRotulo(): string {
    return (
      PERIODOS.find((p) => p.v === this.periodo)?.rot ?? 'Mês atual'
    ).toLowerCase();
  }

  private async carregarPainel(): Promise<void> {
    if (!this.filhoSub) return;
    this.carregandoPainel = true;
    try {
      const { from, to } = this.periodoRange();
      const q = new URLSearchParams({ filho: this.filhoSub, from, to });
      const r = await this.authFetch(`${this.base}/painel?${q}`);
      this.dados = r.ok ? ((await r.json()) as PainelFilho) : undefined;
      if (!r.ok) this.erro = 'Não foi possível carregar os dados do estudante.';
    } catch {
      this.dados = undefined;
    } finally {
      this.carregandoPainel = false;
    }
  }

  private onEscola(e: Event): void {
    this.escolaId = (e.target as HTMLSelectElement).value;
    this.alertas = undefined;
    // Trocar de colégio troca a lista de filhos — reancora no primeiro de lá.
    this.filhoSub = this.filhosDaEscola()[0]?.sub ?? '';
    void this.carregarPainel();
  }

  private onFilho(e: Event): void {
    this.filhoSub = (e.target as HTMLSelectElement).value;
    this.alertas = undefined; // senão ficaria o alerta do filho anterior na tela
    void this.carregarPainel();
  }

  /** Drill do card de alertas: o QUE foi barrado e o que a criança escreveu. */
  private async abrirAlertas(): Promise<void> {
    if (this.alertas) {
      this.alertas = undefined; // segundo clique fecha
      return;
    }
    this.carregandoAlertas = true;
    try {
      const { from, to } = this.periodoRange();
      const q = new URLSearchParams({ filho: this.filhoSub, from, to });
      const r = await this.authFetch(`${this.base}/alertas?${q}`);
      this.alertas = r.ok ? ((await r.json()) as AlertaRow[]) : [];
    } catch {
      this.alertas = [];
    } finally {
      this.carregandoAlertas = false;
    }
  }

  /** O PDF exige Authorization, então não dá pra usar um link direto: buscamos
   * com o token e entregamos o blob ao navegador. */
  private async baixarPdf(): Promise<void> {
    if (!this.filhoSub || this.baixandoPdf) return;
    this.baixandoPdf = true;
    try {
      const { from, to } = this.periodoRange();
      const q = new URLSearchParams({ filho: this.filhoSub, from, to });
      const r = await this.authFetch(`${this.base}/relatorio.pdf?${q}`);
      if (!r.ok) {
        this.erro = 'Não foi possível gerar o relatório agora.';
        return;
      }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const nome =
        this.filhosDaEscola().find((f) => f.sub === this.filhoSub)?.nome ??
        'estudante';
      a.download = `relatorio-${nome.toLowerCase().replace(/\s+/g, '-')}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      // Revoga só depois do clique: revogar antes cancela o download em alguns
      // navegadores.
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch {
      this.erro = 'Não foi possível gerar o relatório agora.';
    } finally {
      this.baixandoPdf = false;
    }
  }

  private onPeriodo(e: Event): void {
    this.periodo = (e.target as HTMLSelectElement).value;
    this.alertas = undefined;
    void this.carregarPainel();
  }

  // ── render ────────────────────────────────────────────────────────────────
  render() {
    if (this.loading) return html`<div class="msg">Carregando…</div>`;
    if (this.erro) return html`<div class="msg err">${this.erro}</div>`;

    const escolas = this.ctx?.escolas ?? [];
    const filhos = this.filhosDaEscola();
    if (!filhos.length) {
      return html`<div class="vazio">
        Ainda não há estudantes vinculados à sua conta.
      </div>`;
    }

    return html`
      <div class="bar">
        ${escolas.length > 1
          ? html`<div class="campo">
              <label>Colégio</label>
              <select @change=${this.onEscola} .value=${this.escolaId}>
                ${escolas.map(
                  (e) => html`<option value=${e.id}>${e.nome}</option>`
                )}
              </select>
            </div>`
          : nothing}
        <div class="campo">
          <label>Estudante</label>
          <select @change=${this.onFilho} .value=${this.filhoSub}>
            ${filhos.map(
              (f) => html`<option value=${f.sub}>${f.nome ?? f.sub}</option>`
            )}
          </select>
        </div>
        <div class="campo">
          <label>Período</label>
          <select @change=${this.onPeriodo} .value=${this.periodo}>
            ${PERIODOS.map(
              (p) =>
                html`<option value=${p.v} ?selected=${this.periodo === p.v}>
                  ${p.rot}
                </option>`
            )}
          </select>
        </div>
      </div>
      ${this.renderPainel(filhos)}
    `;
  }

  private renderPainel(filhos: FilhoRow[]) {
    const d = this.dados;
    if (!d) return html`<div class="msg">Carregando os dados…</div>`;

    const atual = filhos.find((f) => f.sub === this.filhoSub);
    const min = (v: number) => `${Math.round(v)}`;
    const um = (v: number) => (v ? v.toFixed(1).replace('.', ',') : '0');

    return html`
      <div class=${this.carregandoPainel ? 'dim' : ''}>
        <div class="titulo">
          <b>${atual?.nome ?? 'Estudante'}</b>
          <span
            >${d.escola ? `${d.escola} · ` : ''}${this.periodoRotulo()}</span
          >
          <button
            class="btn-pdf"
            ?disabled=${this.baixandoPdf}
            @click=${this.baixarPdf}
            title="Baixa o relatório deste estudante no período selecionado"
          >
            ${this.baixandoPdf ? 'Gerando…' : '↓ Exportar PDF'}
          </button>
        </div>
        <div class="grid">
          ${this.kpi('Sessões com o Tutor', d.sessoes_tutor_7d, {
            sub: `conversas · ${this.periodoRotulo()}`,
            delta: d.delta_sessoes,
            anterior: d.prev_sessoes,
          })}
          ${this.kpi('Perguntas feitas', d.mensagens_alunos_7d, {
            sub: `~${um(d.media_msgs_por_sessao)} por sessão`,
            delta: d.delta_interacoes,
            anterior: d.prev_interacoes,
          })}
          ${this.kpi('Tempo de estudo', min(d.tempo_total_min), {
            unidade: 'min',
            sub: `~${min(d.tempo_medio_sessao_min)} min por sessão`,
          })}
          ${this.kpi('Média por sessão', min(d.tempo_medio_sessao_min), {
            unidade: 'min',
            sub: `em ${d.sessoes_tutor_7d} sessão(ões)`,
          })}
          ${this.kpi('Dias com estudo', um(d.recorrencia_media_dias), {
            unidade: 'dias',
            sub: `dias diferentes · ${this.periodoRotulo()}`,
          })}
          ${this.kpi('Alertas de segurança', d.bloqueios_total, {
            sub: d.bloqueios_total
              ? 'toque para ver o que foi barrado'
              : `nenhum bloqueio · ${this.periodoRotulo()}`,
            delta: d.delta_barrados,
            anterior: d.prev_barrados,
            inverso: true,
            drill: d.bloqueios_total > 0,
          })}
        </div>
        ${this.renderAlertas()}

        <div class="sec">Evolução no período</div>
        ${this.renderEvolucao(d.serie_temporal)}

        <div class="sec">Matérias mais consultadas</div>
        ${this.renderLista(
          (d.por_materia ?? []).map((m) => ({ nome: m.materia, total: m.total }))
        )}

        <div class="sec">Assuntos</div>
        ${this.renderLista(
          (d.por_tematica ?? []).map((t) => ({ nome: t.tematica, total: t.total }))
        )}

        <div class="sec">Assuntos em alta</div>
        ${this.renderEmergentes(d.assuntos_emergentes)}

        <div class="sec">Alertas de segurança por tipo</div>
        ${d.por_categoria_bloqueio?.length
          ? this.renderLista(
              d.por_categoria_bloqueio.map((c) => ({
                nome: c.rotulo || c.categoria,
                total: c.total,
              }))
            )
          : html`<div class="vazio ok">
              Nenhum alerta neste período — o tutor não precisou barrar nada.
            </div>`}
      </div>
    `;
  }

  /** KPI no formato do painel da escola: rótulo, número, linha de contexto e
   * tendência. `inverso` = crescer é RUIM (alertas), então a seta troca de cor.
   * `drill` deixa o card clicável (hoje só alertas de segurança). */
  private kpi(
    rotulo: string,
    valor: number | string,
    o: {
      unidade?: string;
      sub?: string;
      delta?: number | null;
      anterior?: number | null;
      inverso?: boolean;
      drill?: boolean;
    } = {}
  ) {
    const { unidade, sub, delta, anterior, inverso = false, drill = false } = o;
    const temDelta = delta !== null && delta !== undefined;
    const subiu = temDelta && (delta as number) > 0;
    const bom = inverso ? !subiu : subiu;
    const aberto = drill && !!this.alertas;
    return html`<div
      class="kpi ${drill ? 'clicavel' : ''} ${aberto ? 'aberto' : ''}"
      role=${drill ? 'button' : nothing}
      tabindex=${drill ? '0' : nothing}
      @click=${drill ? () => void this.abrirAlertas() : nothing}
      @keydown=${drill
        ? (e: KeyboardEvent) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              void this.abrirAlertas();
            }
          }
        : nothing}
    >
      <div class="rot">${rotulo}${drill ? html`<span class="seta">›</span>` : nothing}</div>
      <div class="val">
        ${valor}${unidade ? html`<span class="un">${unidade}</span>` : nothing}
      </div>
      ${sub ? html`<div class="ssub">${sub}</div>` : nothing}
      ${temDelta && delta !== 0
        ? html`<div class="delta ${bom ? 'up' : 'down'}">
            ${subiu ? '▲' : '▼'} ${Math.abs(delta as number)}%
            <span>vs. ${anterior ?? 0} no período anterior</span>
          </div>`
        : nothing}
    </div>`;
  }

  /** Lista do drill: cada tentativa barrada com o que a criança escreveu. */
  private renderAlertas() {
    if (this.carregandoAlertas) {
      return html`<div class="msg">Carregando os alertas…</div>`;
    }
    if (!this.alertas) return nothing;
    if (!this.alertas.length) {
      return html`<div class="vazio ok">
        Nenhum alerta registrado neste período.
      </div>`;
    }
    return html`<div class="drill">
      ${this.alertas.map(
        (a) => html`<div class="arow">
          <div class="amain">
            <div class="atopo">
              <span class="atag">${a.rotulo}</span>
              <span class="aquando">${this.quando(a.created_at)}</span>
            </div>
            ${a.mensagem
              ? html`<div class="amsg">“${a.mensagem}”</div>`
              : html`<div class="amsg vazia">
                  (a mensagem não ficou registrada)
                </div>`}
          </div>
        </div>`
      )}
    </div>`;
  }

  private quando(iso: string): string {
    const d = new Date(iso);
    return Number.isNaN(d.getTime())
      ? iso
      : d.toLocaleString('pt-BR', {
          day: '2-digit',
          month: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
        });
  }

  /** Evolução: barras por dia/semana (perguntas) com marca de alerta. SVG puro —
   * o painel do pai não carrega biblioteca de gráfico. */
  private renderEvolucao(
    pontos: { data: string; interacoes: number; alertas: number }[] | undefined
  ) {
    if (!pontos?.length) {
      return html`<div class="vazio">
        Ainda não há atividade suficiente para mostrar a evolução.
      </div>`;
    }
    const max = Math.max(...pontos.map((p) => p.interacoes), 1);
    const dia = (iso: string) => {
      const [, m, d] = iso.split('-');
      return d && m ? `${d}/${m}` : iso;
    };
    return html`<div class="card">
      <div class="barras">
        ${pontos.map(
          (p) => html`<div
            class="col"
            title="${dia(p.data)}: ${p.interacoes} pergunta(s)${p.alertas
              ? `, ${p.alertas} alerta(s)`
              : ''}"
          >
            <div class="tubo">
              <div
                class="preenche"
                style="height:${Math.max(
                  4,
                  Math.round((p.interacoes / max) * 100)
                )}%"
              >
                ${p.alertas
                  ? html`<span class="pino" title="${p.alertas} alerta(s)"
                      >${p.alertas}</span
                    >`
                  : nothing}
              </div>
            </div>
            <div class="eixo">${dia(p.data)}</div>
            <div class="qtd">${p.interacoes}</div>
          </div>`
        )}
        <!-- Colunas fantasma: com 1 ou 2 dias, sem isto a barra estica pela
             largura toda do card e parece um bloco de cor, não um gráfico. -->
        ${pontos.length < 7
          ? Array.from(
              { length: 7 - pontos.length },
              () => html`<div class="col fantasma"><div class="tubo"></div></div>`
            )
          : nothing}
      </div>
    </div>`;
  }

  private renderEmergentes(
    itens:
      | { materia: string; atual: number; anterior: number; growth_pct: number | null }[]
      | undefined
  ) {
    if (!itens?.length) {
      return html`<div class="vazio">
        Nada cresceu em relação ao período anterior.
      </div>`;
    }
    return html`<div class="lista">
      ${itens.map(
        (i) => html`<div class="linha">
          <span>${i.materia}</span>
          <b class="alta">
            ${i.anterior === 0
              ? 'novo'
              : `+${i.growth_pct ?? 0}%`}
            <small>${i.anterior} → ${i.atual}</small>
          </b>
        </div>`
      )}
    </div>`;
  }

  private renderLista(itens: { nome: string; total: number }[] | undefined) {
    if (!itens?.length) {
      return html`<div class="vazio">
        Ainda não há registros neste período.
      </div>`;
    }
    return html`<div class="lista">
      ${itens
        .slice(0, 8)
        .map(
          (i) =>
            html`<div class="linha"><span>${i.nome}</span><b>${i.total}</b></div>`
        )}
    </div>`;
  }
}
